import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ProfileNav } from '@components/profile_nav/profile_nav'
import { i18WithParams as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { setElementStylesById } from 'components-ui/src/shared/common'

export const AccountButton = () => {
  const { accountName } = useParams()
  const theme = useTheme()
  const { navigateRoute } = useRoutes()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const contrastColor = theme.palette.getContrastText(theme.palette.primary.main)

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus()
    }

    prevOpen.current = open
  }, [open])

  const handleToggle = useCallback(() => {
    if (!isBelowMD) {
      return navigateRoute('myprofile', { accountName })
    }

    setOpen((prevOpen) => !prevOpen)
  }, [accountName, isBelowMD, navigateRoute])

  const handleClose = useCallback((event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }
    setOpen(false)
  }, [])

  const menuKeyHandler = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const handleNavClick = useCallback(() => {
    setOpen(false)
  }, [])

  return (
    <Fragment>
      <Button
        ref={anchorRef}
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        id="composition-button"
        onClick={handleToggle}
        startIcon={<AccountCircleIcon />}
        sx={{
          padding: 0,
          color: contrastColor,
          ['& .MuiButton-startIcon']: {
            marginRight: isBelowMD ? 0 : '8px',
          },
          ['& svg']: {
            width: 24,
            height: 24,
          },
        }}
      >
        {!isBelowMD && t('header.loggedInButton')}
      </Button>
      {isBelowMD && (
        <Popper
          sx={{ zIndex: theme.zIndex.tooltip, width: '90%' }}
          role={undefined}
          open={open}
          anchorEl={anchorRef.current}
          disablePortal
          placement="bottom-start"
          popperOptions={{
            modifiers: [
              {
                name: 'stackHandler',
                enabled: true,
                phase: 'main',
                effect: () => {
                  // This is a hack to prevent the sticky header from overlapping the popper
                  // when the popper is open. The popper is not a child of the header, so we
                  // can't use z-index to control the stacking order. Instead, we change the
                  // header's position to static when the popper is open, and back to sticky
                  // when the popper is closed.
                  // booking-header is the id of the header element in the booking_header component.
                  // this is only meaningful on mobile devices.
                  setElementStylesById('booking-header', { position: 'static' })
                  return () => setElementStylesById('booking-header', { position: 'sticky' })
                },
              },
            ],
          }}
        >
          <Paper>
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList
                id="composition-menu"
                autoFocusItem={open}
                aria-labelledby="composition-button"
                onKeyDown={menuKeyHandler}
              >
                <ProfileNav onClick={handleNavClick} />
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Popper>
      )}
    </Fragment>
  )
}
